import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { TESTIMONIALS as WALL } from "@/data/testimonials";

// ── Weekly-stepping Syndicate-wide gain counter ────────────────
// Steps up once per week (no live ticking, no pennies).
const GAIN_ANCHOR_TS = new Date("2026-07-27T00:00:00Z").getTime(); // Monday
const GAIN_ANCHOR_VALUE = 353_600; // £353,600 at anchor
const GAIN_WEEKLY_GROWTH = 2_100;  // £/week

function useLiveGain() {
  const compute = () => {
    const weeksElapsed = Math.max(0, Math.floor((Date.now() - GAIN_ANCHOR_TS) / (7 * 86_400_000)));
    return GAIN_ANCHOR_VALUE + GAIN_WEEKLY_GROWTH * weeksElapsed;
  };
  const [value, setValue] = useState(compute);
  useEffect(() => {
    // Re-check hourly so the weekly step lands on-screen without needing a page refresh
    const id = setInterval(() => setValue(compute()), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return value;
}

function formatGain(v) {
  // Whole pounds, no pennies: £353,600
  return "£" + Math.round(v).toLocaleString("en-GB");
}

export default function Testimonials() {
  const [lightbox, setLightbox] = useState(null);
  const liveGain = useLiveGain();

  const stats = [
    { k: "238X", v: "TOP MEMECOIN CALL", live: false },
    { k: formatGain(liveGain), v: "SYNDICATE 30-DAY GAIN", live: true },
    { k: "24/7", v: "LIVE CALL FLOW", live: false },
  ];

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden grain" data-testid="testimonials-page">
      <Navbar />

      <section className="relative pt-36 md:pt-44 pb-16 px-4 md:px-6">
        <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            RESULTS // UNFILTERED
          </p>
          <h1 className="mt-4 font-display text-5xl md:text-7xl tracking-tight">
            Real trades.<br/>Real members.
          </h1>
          <p className="mt-6 text-white/60 max-w-2xl mx-auto text-base md:text-lg">
            Straight from member wallets and our Telegram rooms. No filters, no edited numbers — just receipts.
          </p>
        </div>
      </section>

      {/* Stats bar */}
      <section className="relative z-10 px-4 md:px-6 mb-14">
        <div className="max-w-5xl mx-auto grid grid-cols-3 gap-4">
          {stats.map((s) => (
            <div
              key={s.v}
              data-testid={`stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative frame p-5 md:p-6 text-center"
            >
              {s.live && (
                <div
                  data-testid="live-badge"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600/95 border border-red-400/50 shadow-[0_0_20px_rgba(239,68,68,0.55)] animate-live-flash"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                  </span>
                  <span className="font-mono-serif text-[9px] tracking-[0.3em] text-white font-bold">
                    LIVE
                  </span>
                </div>
              )}
              <div className={`font-display text-2xl md:text-3xl tabular-nums bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent ${s.live ? "animate-gain-pulse" : ""}`}>
                {s.k}
              </div>
              <div className="mt-2 font-mono-serif text-[9px] md:text-[10px] tracking-[0.25em] text-white/50">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PnL & call cards grid — merged into wall below */}

      {/* Feature quote */}
      <section className="relative z-10 px-4 md:px-6 mt-24">
        <div className="max-w-4xl mx-auto frame p-10 md:p-14 text-center">
          <Quote size={32} className="mx-auto text-white/30" />
          <p className="mt-6 font-display text-2xl md:text-4xl leading-snug text-white/90">
            "Since joining The Syndicate, I've learned more in months than I did in years trading alone. Real people, real trades, real edge."
          </p>
          <p className="mt-6 font-mono-serif text-[10px] tracking-[0.3em] text-white/50">— SYNDICATE MEMBER</p>
        </div>
      </section>

      {/* THE WALL — every testimonial in one place */}
      <section className="relative z-10 mt-24 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">01 // THE WALL</p>
          <h2 className="mt-2 font-display text-3xl md:text-5xl">Every testimonial. All in one place.</h2>
          <p className="mt-4 text-white/55 max-w-2xl">
            The complete wall of member proof — trade receipts, wallet flips, forex wins, memecoin plays and life upgrades. Tap any image to enlarge.
          </p>

          <div className="mt-10 columns-1 sm:columns-2 md:columns-3 gap-5 md:gap-6 [column-fill:_balance]">
            {WALL.map((src, i) => (
              <button
                key={i}
                data-testid={`wall-testimonial-${i}`}
                onClick={() => setLightbox(src)}
                className="group mb-5 md:mb-6 block w-full break-inside-avoid rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition"
              >
                <img
                  src={src}
                  alt={`Member testimonial ${i + 1}`}
                  className="w-full h-auto block group-hover:scale-[1.02] transition duration-700"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 px-4 md:px-6 mt-24">
        <div className="max-w-3xl mx-auto text-center frame p-10 md:p-14">
          <h2 className="font-display text-4xl md:text-5xl">Your name on the next screenshot?</h2>
          <p className="mt-4 text-white/60">Join The Syndicate and start trading with the pack.</p>
          <Link
            to="/pricing"
            data-testid="testimonials-join-btn"
            className="mt-8 inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-xs tracking-[0.3em] px-8 py-4 rounded-full hover:opacity-95 transition"
          >
            JOIN NOW <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img src={lightbox} alt="Enlarged" className="max-h-[92vh] max-w-full rounded-xl border border-white/10" />
        </div>
      )}

      <Footer />
    </div>
  );
}
